import { FC, lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { withAuth } from '@/components/hoc/withAuth';
import { ConditionalRedirect } from '@/components/conditional-redirect';

import DefaultLayout, { Loader } from '../components/layout/DefaultLayout';
import LandingLayout from '../components/layout/LandingLayout';
import AuthLayout from '../components/layout/AuthLayout';
import { ROUTES } from 'src/types/routes';

import ActivationLayout from '@/components/layout/ActivationLayout';
import { PrivacyAndPolicy } from '@/components/pages/privacy-policy';
import MyProfileContact from './my-profile/contact';
import MyProfileFinancialInformation from './my-profile/financial-information';
import MyProfileTrainingSupport from './my-profile/training-support';
import { TermsAndConditions } from '@/components/pages/terms-condition';
import { AdminAuthLayout, AdminLayout } from '@/components/layout/AdminLayout';
import TermsOfUseConsultant from '@/components/pages/terms-of-use/consultant';
import TermsOfUseFranchisor from '@/components/pages/terms-of-use/franchisor';
import AdminBrandNames from './admin/brand-names';
import EditDetailsPageConsultant from './admin/consultants/EditDatailsPage';
import EditDetailsPageFranchisor from './admin/franchisers/EditDetailsPage';
import CreateDetailsPageFranchisor from './admin/franchisers/CreateDetailsPage';
import CreateDetailsPageConsultant from './admin/consultants/CreateDetailsPage';
import AdminBookingsDetailedPage from './admin/bookings/DetailedPage';
import AdminReviews from './admin/review';
import AdminDefaultLayout from '@/components/layout/AdminLayout/AdminDefaultLayout';
import AdminProfile from './admin/profile';
import AdminBookings from './admin/bookings';
import AdminPayments from './admin/payments';
import AdminPromotions from './admin/promotions';
import AdminApiKeys from './admin/api-keys';
import AdminAdministrators from './admin/administrators';
import AdminConsultantsDetailedPage from './admin/consultants/DetailedPage';
import { AdminFranchisesDetailedPage } from './admin/franchisers';
import AdminBrandMappingManagement from './admin/brand-mapping-management';
import AdminEmailManagement from './admin/email-management';

const AdminSettings = lazy<FC>(() => import('./admin/settings'));
const AdminLegalDocuments = lazy<FC>(() => import('./admin/legal-documents'));
const AdminPopup = lazy<FC>(() => import('./admin/popup'));
const AdminLegalDocumentEditor = lazy<FC>(
  () => import('./admin/legal-documents/editor'),
);
const AdminFreeListing = lazy<FC>(() => import('./admin/free-listing'));

const SignIn = lazy<FC>(() => import('./sign-in'));
const SignUp = lazy<FC>(() => import('./sign-up'));

const Consultants = lazy<FC>(() => import('./franchise/find-consultant'));
const Franchisors = lazy<FC>(() => import('./consultant/find-franchisor'));

const MyProfile = lazy<FC>(() => import('./my-profile'));
const MyProfileGeneral = lazy<FC>(() => import('./my-profile/general'));
const MyProfileCalendar = lazy<FC>(
  () => import('./my-profile/calendar/calendar'),
);
const MyProfileChangePassword = lazy<FC>(
  () => import('./my-profile/change-password'),
);
const MyProfilePayments = lazy<FC>(() => import('./my-profile/payments'));
const MyProfileConsultantPayments = lazy<FC>(
  () => import('./my-profile/payments/ConsultantPayment'),
);
const MyProfilePayout = lazy<FC>(() => import('./my-profile/payments'));
const MyProfileSubscription = lazy<FC>(
  () => import('./my-profile/subscription'),
);
const MyProfileBilling = lazy<FC>(() => import('./my-profile/billing'));
const MyProfileReferralFee = lazy<FC>(
  () => import('./my-profile/referral-fee'),
);
const MyProfileReferralTerms = lazy<FC>(
  () => import('./my-profile/referral-terms'),
);
const MyProfileAccountStatus = lazy<FC>(
  () => import('./my-profile/account-status'),
);
const MyBookings = lazy<FC>(() => import('./my-bookings'));
const Booking = lazy<FC>(() => import('./booking'));

const ConsultantProfile = lazy<FC>(
  () => import('./franchise/consultant-profile'),
);

const FranchisorProfile = lazy<FC>(
  () => import('./franchise/franchisor-profile'),
);
const Payments = lazy<FC>(() => import('./payments'));
// const Dashboard = lazy<FC>(() => import('./dashboard'));
const Home = lazy<FC>(() => import('./home'));
const TerritoryScout = lazy<FC>(() => import('./territory-scout'));

const Chats = lazy<FC>(() => import('./chats'));
const Chat = lazy<FC>(() => import('./chats/chat'));
const ActivateProfile = lazy<FC>(() => import('./activate-profile'));
const ActivateProfileFranchisor = lazy<FC>(
  () => import('./activate-profile-franchisor'),
);

const AdminFranchises = lazy<FC>(() => import('./admin/franchisers/TablePage'));
const AdminConsultants = lazy<FC>(
  () => import('./admin/consultants/TablePage'),
);
const AdminDashboard = lazy<FC>(() => import('./admin/dashboard'));
const AdminForgotPassword = lazy<FC>(
  () => import('./admin/auth/forgot-password'),
);
const AdminSignIn = lazy<FC>(() => import('./admin/auth/sign-in'));
const AdminConsultantRef = lazy<FC>(
  () => import('./admin/consultant-reference'),
);

const routes: RouteObject[] = [
  {
    Component: LandingLayout,
    loader: () => <Loader spinning />,
    children: [
      {
        Component: Home,
        loader: () => <Loader spinning />,
        index: true,
      },
    ],
  },
  {
    Component: withAuth(DefaultLayout),
    loader: () => <Loader spinning />,
    children: [
      {
        element: <Navigate to={ROUTES.MY_PROFILE} />,
        index: true,
      },
      {
        Component: MyProfile,
        path: ROUTES.MY_PROFILE,
        children: [
          {
            path: '',
            element: <ConditionalRedirect to={ROUTES.MY_PROFILE_GENERAL} />,
          },
          {
            Component: MyProfileGeneral,
            path: ROUTES.MY_PROFILE_GENERAL,
          },
          {
            Component: MyProfileCalendar,
            path: ROUTES.MY_PROFILE_CALENDAR,
          },
          {
            Component: MyProfileChangePassword,
            path: ROUTES.MY_PROFILE_CHANGE_PASSWORD,
          },
          {
            Component: MyProfilePayments,
            path: ROUTES.MY_PROFILE_PAYMENT,
          },
          {
            Component: MyProfileConsultantPayments,
            path: ROUTES.MY_PROFILE_PAYMENT_DETAILS,
          },
          {
            Component: MyProfilePayout,
            path: ROUTES.MY_PROFILE_PAYOUT,
          },
          {
            Component: MyProfileSubscription,
            path: ROUTES.MY_PROFILE_SUBSCRIPTION,
          },
          {
            Component: MyProfileBilling,
            path: ROUTES.MY_PROFILE_BILLING,
          },
          // {
          //   Component: MyProfileAccountStatus,
          //   path: ROUTES.MY_PROFILE_ACCOUNT_STATUS,
          // },
          {
            Component: MyProfileContact,
            path: ROUTES.MY_PROFILE_CONTACT,
          },
          {
            Component: MyProfileFinancialInformation,
            path: ROUTES.MY_PROFILE_FINANCIAL,
          },
          {
            Component: MyProfileTrainingSupport,
            path: ROUTES.MY_PROFILE_TRAINING,
          },
          {
            Component: MyProfileReferralFee,
            path: ROUTES.MY_PROFILE_REFERRAL_FEE,
          },
          {
            Component: MyProfileReferralTerms,
            path: ROUTES.MY_PROFILE_REFERRAL_TERMS,
          },
        ],
      },
      {
        Component: MyBookings,
        path: ROUTES.MY_BOOKINGS,
        index: true,
      },
      {
        Component: Booking,
        path: ROUTES.BOOKING,
        index: true,
      },
      {
        Component: ConsultantProfile,
        path: ROUTES.CONSULTANT,
        index: true,
      },
      {
        Component: FranchisorProfile,
        path: ROUTES.FRANCHISOR,
        index: true,
      },
      {
        Component: Consultants,
        path: ROUTES.FIND_CONSULTANT,
        index: true,
      },
      {
        Component: Franchisors,
        path: ROUTES.FIND_FRANCHISORS,
        index: true,
      },
      // {
      //   Component: Dashboard,
      //   path: ROUTES.DASHBOARD,
      //   index: true,
      // },
      {
        Component: Payments,
        path: ROUTES.PAYMENTS,
        index: true,
      },
      {
        Component: TerritoryScout,
        path: ROUTES.TERRITORY_SCOUT,
        index: true,
      },
      {
        Component: Chats,
        path: ROUTES.CHATS,
        children: [
          {
            Component: Chat,
            path: ROUTES.CHAT,
          },
          {
            Component: Outlet,
            path: '*',
          },
        ],
      },
      {
        element: <Navigate to={ROUTES.HOME} />,
        path: '*',
      },
    ],
  },
  {
    Component: AuthLayout,
    loader: () => <Loader spinning />,
    children: [
      {
        Component: SignIn,
        path: ROUTES.SIGN_IN,
        index: true,
      },
      {
        Component: SignUp,
        path: ROUTES.SIGN_UP,
        index: true,
      },
    ],
  },
  {
    Component: ActivationLayout,
    loader: () => <Loader spinning />,
    children: [
      {
        Component: ActivateProfile,
        path: ROUTES.ACTIVATE_PROFILE,
        index: true,
      },
    ],
  },
  {
    Component: ActivationLayout,
    loader: () => <Loader spinning />,
    children: [
      {
        Component: ActivateProfileFranchisor,
        path: ROUTES.ACTIVATE_PROFILE_FRANCHISOR,
        index: true,
      },
    ],
  },

  {
    Component: AdminLayout,
    loader: () => <Loader spinning />,
    children: [
      {
        Component: AdminAuthLayout,
        loader: () => <Loader spinning />,
        children: [
          {
            Component: AdminSignIn,
            path: ROUTES.ADMIN_DASHBOARD_SIGN_IN,
            index: true,
          },
        ],
      },
      {
        Component: withAuth(AdminDefaultLayout),
        loader: () => <Loader spinning />,
        children: [
          {
            Component: AdminProfile,
            path: ROUTES.ADMIN_PROFILE,
            index: true,
          },
          {
            Component: AdminDashboard,
            path: ROUTES.ADMIN_DASHBOARD,
            index: true,
          },
          {
            Component: AdminBookings,
            path: ROUTES.ADMIN_BOOKINGS,
            index: true,
          },
          {
            Component: AdminPayments,
            path: ROUTES.ADMIN_PAYMENTS,
            index: true,
          },
          {
            Component: AdminConsultants,
            path: ROUTES.ADMIN_CONSULTANTS,
            index: true,
          },
          {
            Component: AdminPromotions,
            path: ROUTES.ADMIN_PROMOTIONS,
            index: true,
          },
          {
            Component: AdminApiKeys,
            path: ROUTES.ADMIN_API_KEYS,
            index: true,
          },
          {
            Component: AdminAdministrators,
            path: ROUTES.ADMIN_ADMINISTRATORS,
            index: true,
          },
          {
            Component: AdminConsultantsDetailedPage,
            path: ROUTES.ADMIN_CONSULTANTS_DETAILED_PAGE,
            index: true,
          },
          {
            Component: AdminFranchisesDetailedPage,
            path: ROUTES.ADMIN_FRANCHISES_DETAILED_PAGE,
            index: true,
          },
          {
            Component: AdminBookingsDetailedPage,
            path: ROUTES.ADMIN_BOOKINGS_DETAILED_PAGE,
            index: true,
          },
          {
            Component: AdminFranchises,
            path: ROUTES.ADMIN_FRANCHISES,
            index: true,
          },
          {
            Component: AdminBrandNames,
            path: ROUTES.ADMIN_BRAND_NAMES,
            index: true,
          },
          {
            Component: AdminReviews,
            path: ROUTES.ADMIN_REVIEWS,
            index: true,
          },
          {
            Component: EditDetailsPageConsultant,
            path: ROUTES.ADMIN_CONSULTANTS_EDIT_DETAILS,
            index: true,
          },
          {
            Component: EditDetailsPageFranchisor,
            path: ROUTES.ADMIN_FRANCHISES_EDIT_DETAILS,
            index: true,
          },
          {
            Component: CreateDetailsPageFranchisor,
            path: ROUTES.ADMIN_FRANCHISES_CREATE_DETAILS,
            index: true,
          },
          {
            Component: CreateDetailsPageConsultant,
            path: ROUTES.ADMIN_CONSULTANTS_CREATE_DETAILS,
            index: true,
          },
          {
            Component: AdminConsultantRef,
            path: ROUTES.ADMIN_CONSULTANT_REFERENCE,
            index: true,
          },
          {
            Component: AdminBrandMappingManagement,
            path: ROUTES.ADMIN_BRAND_MAPPING_MANAGEMENT,
            index: true,
          },
          {
            Component: AdminEmailManagement,
            path: ROUTES.ADMIN_EMAIL_MANAGEMENT,
            index: true,
          },
          {
            Component: AdminSettings,
            path: ROUTES.ADMIN_SETTINGS,
            index: true,
          },
          {
            Component: AdminLegalDocuments,
            path: ROUTES.ADMIN_LEGAL_DOCUMENTS,
            index: true,
          },
          {
            Component: AdminLegalDocumentEditor,
            path: ROUTES.ADMIN_LEGAL_DOCUMENTS_EDIT,
            index: true,
          },
          {
            Component: AdminFreeListing,
            path: ROUTES.ADMIN_FREE_LISTING,
            index: true,
          },
          {
            Component: AdminPopup,
            path: ROUTES.ADMIN_POPUP,
            index: true,
          },
        ],
      },
    ],
  },
  {
    Component: PrivacyAndPolicy,
    path: ROUTES.PRIVACY_AND_POLICY,
    index: true,
  },
  {
    Component: TermsAndConditions,
    path: ROUTES.TERMS_AND_CONDITIONS,
    index: true,
  },
  {
    Component: TermsOfUseConsultant,
    path: ROUTES.TERMS_OF_USE_CONSULTANT,
    index: true,
  },
  {
    Component: TermsOfUseFranchisor,
    path: ROUTES.TERMS_OF_USE_FRANCHISOR,
    index: true,
  },
];

export default routes;
